import { FC } from 'react';
import { ALERT_POSITION, IPlanCard } from '@belong/types';
import { OFFER_TEMPLATE_TYPE } from '@belong/offers';
import { Callout, InfoBox, OfferAlertContainer } from '../../../feedback';
import { Stack } from '../../../ui';
import { PLAN_CARD_TEST_ID } from '../testIds';
import * as styles from '../PlanCard.styles';
import { IMultipleAccordionPlanCardProps } from '../types';
import { ControlledHeightContainer, createRefHandler } from '../ControlledHeightContainer';

/**
 * Display the Callout / Alert / Offer for a Plan
 * ----------------------------------------------
 * NBN: PromoCallout (hard coded in the CMS) is currently how the NBN Offers are added to the plan cards
 * MOBILE: Offer overrides the planCallout (OR promoCallout) specified in the CMS (will never exist at the same time)
 */
export const PlanOffersAndAlerts: FC<Partial<IPlanCard & IMultipleAccordionPlanCardProps>> = props => {
  const {
    calloutHeight = 'auto',
    alertHeight = 'auto',
    removeCalloutHeight,
    removeAlertHeight,
    columnIndex = 0
  } = props;
  const { promoCallout, planCardAlert, offerDetails } = props;
  const { calloutRefs, alertRefs } = props;

  // Alert/Callout spacers are shown to align all column content
  const hasCalloutSpacer = calloutHeight !== '0px';
  const hasAlertSpacer = alertHeight !== '0px';

  // ! Mobile Offers and NBN Offers should never exist at the same time, but
  // ! Mobile Offers will override the NBN Offer until NBN adopts the Offer Engine
  const hasMobileOffer = Boolean(offerDetails);
  const hasNBNOffer = Boolean(promoCallout);
  const hasPlanAlert = Boolean(planCardAlert);

  const showNBNOffer = hasNBNOffer && !hasMobileOffer;
  const showCalloutSpacer = hasCalloutSpacer && !hasNBNOffer && !hasMobileOffer;
  const showAlertSpacer = hasAlertSpacer && !hasPlanAlert;

  // If not showing anything, then just return empty
  if (!hasMobileOffer && !hasNBNOffer && !hasPlanAlert && !showCalloutSpacer && !showAlertSpacer) {
    return null;
  }

  // Forced element heights
  const forcedCalloutHeight = hasCalloutSpacer && !removeCalloutHeight ? calloutHeight : undefined;
  const forcedAlertHeight = hasAlertSpacer && !removeAlertHeight ? alertHeight : undefined;

  return (
    <Stack alignX="center" space={{ xs: 'xxsmall', md: 'xsmall' }} data-testid={PLAN_CARD_TEST_ID.offersAndAlerts}>
      {showNBNOffer && promoCallout && (
        <ControlledHeightContainer
          data-testid="promo"
          height={forcedCalloutHeight}
          ref={createRefHandler(columnIndex, calloutRefs)}
        >
          <Callout
            data-testid={PLAN_CARD_TEST_ID.planCallout}
            colour={promoCallout.colour}
            title={promoCallout.title}
            icon={promoCallout.icon}
            description={promoCallout.description}
            backgroundImage={promoCallout.backgroundImage}
            fullwidth
          />
        </ControlledHeightContainer>
      )}
      {offerDetails && (
        <ControlledHeightContainer
          data-testid="offer"
          height={forcedCalloutHeight}
          ref={createRefHandler(columnIndex, calloutRefs)}
        >
          <OfferAlertContainer templateType={OFFER_TEMPLATE_TYPE.PLAN_CARD} offerOrReward={offerDetails} />
        </ControlledHeightContainer>
      )}
      {showCalloutSpacer && (
        <styles.Skeleton skeletonHeight={calloutHeight} data-testid={PLAN_CARD_TEST_ID.planCalloutSkeleton} />
      )}
      {planCardAlert && (
        <ControlledHeightContainer
          data-testid="alert"
          height={forcedAlertHeight}
          ref={createRefHandler(columnIndex, alertRefs)}
        >
          <InfoBox {...planCardAlert} data-testid={PLAN_CARD_TEST_ID.planAlert} position={ALERT_POSITION.BLOCK} />
        </ControlledHeightContainer>
      )}
      {showAlertSpacer && (
        <styles.Skeleton skeletonHeight={alertHeight} data-testid={PLAN_CARD_TEST_ID.planAlertSkeleton} />
      )}
    </Stack>
  );
};

PlanOffersAndAlerts.displayName = 'PlanOffersAndAlerts';
